import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";

const BRAND = { orange: "#F35A3A", dark: "var(--wp-panel-text-primary, #19242F)" };

function productId(product = {}) {
  return String(product.id || product._id || "");
}

function productName(product = {}) {
  return product.name || product.nombre || "Sin nombre";
}

function category(product = {}) {
  return String(product.category || product.categoria || "General").trim() || "General";
}

function effectiveSection(product = {}) {
  return String(product.gastronomicMenu?.section || "").trim() || category(product);
}

function sortProducts(left, right) {
  return Number(left.gastronomicMenu?.sortOrder || 0) - Number(right.gastronomicMenu?.sortOrder || 0)
    || productName(left).localeCompare(productName(right), "es");
}

function buildDraft(products = []) {
  const map = new Map();
  products
    .filter((product) => product.gastronomicMenu?.enabled === true)
    .forEach((product) => {
      const label = effectiveSection(product);
      if (!map.has(label)) map.set(label, { label, order: Number(product.gastronomicMenu?.sectionOrder || 0), products: [] });
      const section = map.get(label);
      section.order = Math.min(section.order, Number(product.gastronomicMenu?.sectionOrder || 0));
      section.products.push(product);
    });
  return [...map.values()]
    .map((section) => ({ ...section, products: [...section.products].sort(sortProducts) }))
    .sort((left, right) => left.order - right.order || left.label.localeCompare(right.label, "es"));
}

function move(list, index, direction) {
  const target = index + direction;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

export default function GastronomicMenuOrderDialog({ open, onClose, products = [], canEdit = false, saving = false, error = "", onSave }) {
  const mobile = useMediaQuery("(max-width:599.95px)", { noSsr: true });
  const [draft, setDraft] = React.useState([]);

  React.useEffect(() => {
    if (open) setDraft(buildDraft(products));
  }, [open, products]);

  const moveSection = (index, direction) => setDraft((current) => move(current, index, direction));
  const moveProduct = (sectionIndex, productIndex, direction) => {
    setDraft((current) => current.map((section, index) => index === sectionIndex
      ? { ...section, products: move(section.products, productIndex, direction) }
      : section));
  };

  const updates = draft.flatMap((section, sectionIndex) => section.products.map((product, productIndex) => ({
    id: productId(product),
    gastronomicMenu: {
      ...(product.gastronomicMenu || {}),
      section: String(product.gastronomicMenu?.section || "").trim(),
      sectionOrder: sectionIndex,
      sortOrder: productIndex,
    },
  })));

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md" fullScreen={mobile} PaperProps={{ sx: { borderRadius: 4 } }}>
      <DialogTitle sx={{ fontWeight: 950, color: BRAND.dark }}>Ordenar Carta</DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)" }}>
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            El orden se guarda en los campos actuales de la Carta. Usá Subir/Bajar: funciona con mouse, touch y teclado y no depende de arrastrar.
          </Typography>
          {!canEdit ? <Alert severity="info">Podés ver el orden actual, pero tu rol no tiene permiso para modificarlo.</Alert> : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
          {!draft.length ? <Alert severity="info">Todavía no hay productos incluidos en la Carta.</Alert> : null}
          {draft.map((section, sectionIndex) => (
            <Paper key={section.label} variant="outlined" sx={{ p: 1.4, borderRadius: 3 }}>
              <Stack spacing={1.1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <DragIndicatorRoundedIcon sx={{ color: "text.disabled" }} />
                  <Typography sx={{ flex: 1, fontWeight: 950, color: BRAND.dark }}>{section.label}</Typography>
                  <Chip size="small" label={`${section.products.length} producto${section.products.length === 1 ? "" : "s"}`} />
                  {canEdit ? (
                    <Stack direction="row" spacing={0.3}>
                      <Tooltip title="Subir sección"><span><IconButton size="small" disabled={sectionIndex === 0 || saving} onClick={() => moveSection(sectionIndex, -1)} aria-label={`Subir sección ${section.label}`}><ArrowUpwardRoundedIcon fontSize="small" /></IconButton></span></Tooltip>
                      <Tooltip title="Bajar sección"><span><IconButton size="small" disabled={sectionIndex === draft.length - 1 || saving} onClick={() => moveSection(sectionIndex, 1)} aria-label={`Bajar sección ${section.label}`}><ArrowDownwardRoundedIcon fontSize="small" /></IconButton></span></Tooltip>
                    </Stack>
                  ) : null}
                </Stack>
                <Stack spacing={0.7}>
                  {section.products.map((product, productIndex) => (
                    <Box key={productId(product) || productName(product)} sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, borderRadius: 2, bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.035)" }}>
                      <Typography variant="body2" sx={{ flex: 1, minWidth: 0, fontWeight: 800 }} noWrap>{productName(product)}</Typography>
                      {product.gastronomicMenu?.available === false ? <Chip size="small" label="No disponible" color="warning" variant="outlined" /> : null}
                      {canEdit ? (
                        <Stack direction="row" spacing={0.2}>
                          <IconButton size="small" disabled={productIndex === 0 || saving} onClick={() => moveProduct(sectionIndex, productIndex, -1)} aria-label={`Subir ${productName(product)}`}><ArrowUpwardRoundedIcon fontSize="small" /></IconButton>
                          <IconButton size="small" disabled={productIndex === section.products.length - 1 || saving} onClick={() => moveProduct(sectionIndex, productIndex, 1)} aria-label={`Bajar ${productName(product)}`}><ArrowDownwardRoundedIcon fontSize="small" /></IconButton>
                        </Stack>
                      ) : null}
                    </Box>
                  ))}
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none", fontWeight: 850 }}>Cerrar</Button>
        {canEdit ? <Button variant="contained" onClick={() => onSave(updates)} disabled={saving || !updates.length} sx={{ bgcolor: BRAND.orange, textTransform: "none", fontWeight: 950, "&:hover": { bgcolor: "#D9472B" } }}>{saving ? "Guardando orden…" : "Guardar orden"}</Button> : null}
      </DialogActions>
    </Dialog>
  );
}
